import React, { useState } from 'react';
import '../styles/SuperheroTransform.css';

interface SuperheroTransformProps {
  faceImageUrl: string | null;
  onSuccess?: (superheroImageUrl: string) => void;
  onError?: (error: string) => void;
}

const SuperheroTransform: React.FC<SuperheroTransformProps> = ({ 
  faceImageUrl, 
  onSuccess,
  onError
}) => {
  const [description, setDescription] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [superheroImage, setSuperheroImage] = useState<string | null>(null);
  
  // Predefined superhero description options
  const descriptionOptions = [
    'Superman',
    'The Green Lantern',
    'Batman',
    'Wonder Woman',
    'The Flash',
    'Aquaman',
    'Cyborg',
    'The Atom',
    'The Martian Manhunter'
  ];
  
  const selectDescription = (text: string) => {
    setDescription(text);
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!faceImageUrl) {
      setError('You need to capture or upload a face image first');
      onError?.('No face image provided');
      return;
    }
    
    if (!description) {
      setError('Please enter a superhero description');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/superhero', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          faceUrl: faceImageUrl,
          poseUrl: 'https://superselfie.meshamatic.com/pose3.jpg',
          description: description
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to transform image');
      }
      
      // Set the superhero image URL
      if (data.r2Url) {
        // Use the r2Url directly as it now includes the protocol
        setSuperheroImage(data.r2Url);
        onSuccess?.(data.r2Url);
        console.log('Received R2 URL:', data.r2Url);
      } else {
        setSuperheroImage(data.originalImageUrl);
        onSuccess?.(data.originalImageUrl);
        console.log('Using original image URL as fallback:', data.originalImageUrl);
      }
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  
  const resetForm = () => {
    setSuperheroImage(null);
    setDescription('');
    setError(null);
  };
  
  return (
    <div className="superhero-transform">
      {!superheroImage ? (
        <>
          <h2>Transform into a Superhero</h2>
          {faceImageUrl ? (
            <div className="face-preview">
              <h3>Your Face Image</h3>
              <img src={faceImageUrl} alt="Face" className="face-thumbnail" />
            </div>
          ) : (
            <div className="no-face-warning">
              Please capture or upload a face image first
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="transform-form">
            <div className="form-section">
              <h3>Describe Your Superhero</h3>
              <div className="description-options">
                {descriptionOptions.map((desc) => (
                  <div 
                    key={desc}
                    className={`description-chip ${description === desc ? 'selected' : ''}`}
                    onClick={() => selectDescription(desc)}
                  >
                    {desc}
                  </div>
                ))}
              </div>
              <div className="custom-description-input">
                <label>Or enter a custom description:</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe your superhero powers, appearance, etc."
                  rows={3}
                />
              </div>
            </div>
            
            {error && (
              <div className="error-message">
                <span>⚠️</span> {error}
              </div>
            )}
            
            <button 
              type="submit" 
              className="transform-button"
              disabled={isLoading || !faceImageUrl || !description}
            >
              {isLoading ? 'Transforming...' : 'Transform into Superhero'}
            </button>
          </form>
        </>
      ) : (
        <div className="result-container">
          <h2>Your Superhero Transformation</h2>
          <div className="result-image-container">
            <img src={superheroImage} alt="Superhero Transformation" className="superhero-result" />
          </div>
          <div className="result-actions">
            <a 
              href={superheroImage} 
              download="my-superhero.jpg"
              className="download-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              Download Image
            </a>
            <button onClick={resetForm} className="new-transform-button">
              Create Another
            </button>
          </div>
        </div>
      )}
      
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Transforming your image into a superhero...</p>
          <p className="loading-subtext">This may take up to 10 minutes</p>
        </div>
      )}
    </div>
  );
};

export default SuperheroTransform;